import { SiteTabs } from "app/pages/website-analysis/audience-overlap/SharedVisitorsTable/SiteTabs/SiteTabs";
import type { FunctionComponent } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { SWReactTableOptimized } from "app/components/React/Table/SWReactTableOptimized";
import { getColumns } from "app/pages/website-analysis/audience-overlap/SharedVisitorsTable/columns";
import { AccordionWrapper } from "app/components/Accordion/AccordionWrapper";
import type { ISiteLegendItem } from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelAnalysisChart/ChannelAnalysisChartContainer";
import type { ITableData } from "./getTableData";
import { getTableData } from "./getTableData";
import type { IOrderBy } from "app/components/React/Table/Utils/sort";
import { tableSorter } from "app/components/React/Table/Utils/sort";
import { TableLoader } from "app/pages/website-analysis/audience-overlap/SharedVisitorsTable/TableLoader";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { allTrackers } from "app/services/track/track";
import type { IVennChartData } from "app/pages/website-analysis/audience-overlap/types";
import {
    ExcelDownloadContainer,
    TableContainer,
    SiteTabsContainer,
    MainDomain,
} from "app/pages/website-analysis/audience-overlap/SharedVisitorsTable/styled";
import { i18nFilter } from "app/filters/i18nFilter";
import { buildChosenLegendItems } from "./utils";
import type { IData } from "./sharedTypes";
import { swSettings } from "app/common/services/swSettings";
interface ISharedVisitorsTableProps {
    rawData: IData;
    chosenSitesLegends: ISiteLegendItem[];
    isLoading: boolean;
    vennChartData: IVennChartData;
    excelLink: string;
    vennDiagramChart: any;
    isSingleMode: boolean;
}

const collapseHeight = "240px";

export const SharedVisitorsTable: FunctionComponent<ISharedVisitorsTableProps> = (props) => {
    const {
        rawData,
        isLoading,
        vennChartData = [],
        excelLink,
        vennDiagramChart,
        isSingleMode,
        chosenSitesLegends: chosenSitesLegendsProp,
    } = props;

    const translate = useMemo(() => {
        return i18nFilter();
    }, []);

    const chosenSitesLegends = buildChosenLegendItems(vennChartData, chosenSitesLegendsProp);
    const downloadExcelPermitted = swSettings.current.resources.IsExcelAllowed;

    const [selectedIndex, setSelectedIndex] = useState<number>(0);
    const [orderBy, setOrderBy] = useState<IOrderBy>({
        field: "overlappedDomains",
        sortDirection: "desc",
    });
    const [expendHeight, setExpendHeight] = useState<string>();
    const tableContainerRef = useRef(undefined);

    useEffect(() => {
        setExpendHeight(`${tableContainerRef.current?.clientHeight + 40}px`);
    }, [rawData]);
    const siteItems = chosenSitesLegends.map(({ color, name, isAutoSuggest }) => ({
        displayText: name,
        color,
        isAutoSuggest,
    }));
    const tableData: ITableData = useMemo(() => {
        if (isLoading || !rawData || !rawData.Data || Object.values(rawData.Data).length === 0) {return { Data: [] };}
        const { displayText: selectedSite } = siteItems[selectedIndex];
        return {
            Data: getTableData(rawData, selectedSite, chosenSitesLegends, vennChartData).sort(tableSorter(orderBy)),
        };
    }, [selectedIndex, rawData, orderBy]);

    const onTabClickCallback = (tabIndex) => {
        setSelectedIndex(tabIndex);
        allTrackers.trackEvent("Metric Button", "click", `Overlap Table Tabs/${tabIndex}`);
    };
    const onSort = ({ field, sortDirection }) => {
        setOrderBy({ field, sortDirection });
    };
    const onAccordionToggle = (isExpending) => {
        if (!isExpending) {
            vennDiagramChart?.scrollIntoView({ behavior: "smooth" });
        }
        allTrackers.trackEvent("Expand Collapse", "click", `${isExpending ? "expand" : "collapse"}`);
    };
    return (
        <TableContainer>
            {!isLoading && (
                <SiteTabsContainer>
                    <MainDomain>{translate("audience.overlap.table.tabs.main.domain")}</MainDomain>
                    <SiteTabs
                        siteItems={siteItems}
                        onTabClickCallback={onTabClickCallback}
                        selectedIndex={selectedIndex}
                    />
                    <ExcelDownloadContainer>
                        <DownloadButtonMenu
                            Excel={true}
                            downloadUrl={excelLink}
                            excelLocked={!downloadExcelPermitted}
                        />
                    </ExcelDownloadContainer>
                </SiteTabsContainer>
            )}
            {isLoading ? (
                <TableLoader height={collapseHeight} />
            ) : (
                <AccordionWrapper
                    collapseHeight={collapseHeight}
                    expendHeight={expendHeight}
                    enabled={chosenSitesLegends.length > 3}
                    onToggle={onAccordionToggle}
                >
                    <div ref={tableContainerRef}>
                        <SWReactTableOptimized
                            tableData={tableData}
                            tableColumns={getColumns(orderBy, isSingleMode)}
                            onSort={onSort}
                        />
                    </div>
                </AccordionWrapper>
            )}
        </TableContainer>
    );
};
